import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject } from '@angular/core';
import { WIN_TOAST_AUTO_DISMISS_MS } from '@core/constants/win-toast.constants';
import { WinToastService } from '../win-toast.service';
import { WinToastPushListener } from '../win-toast-push.listener';
import { WinToastComponent } from '../win-toast/win-toast.component';

@Component({
  selector: 'app-win-toast-container',
  standalone: true,
  imports: [WinToastComponent],
  templateUrl: './win-toast-container.component.html',
  styleUrl: './win-toast-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WinToastContainerComponent {
  protected readonly service = inject(WinToastService);

  private readonly dismissTimers = new Map<
    string,
    { id: ReturnType<typeof setTimeout> | null; remaining: number; startTime: number }
  >();
  private hovering = false;

  constructor() {
    // Mounted once in app.component.html — this is where the BE push subscription starts.
    inject(WinToastPushListener).start();

    const destroyRef = inject(DestroyRef);

    effect(() => {
      const ids = new Set(this.service.items().map((t) => t.id));
      // Reset hover state when all toasts are gone (stack div removed from DOM,
      // mouseleave never fires, so the flag must be cleared here).
      if (ids.size === 0) this.hovering = false;
      for (const [id] of this.dismissTimers) {
        if (!ids.has(id)) {
          const e = this.dismissTimers.get(id)!;
          if (e.id !== null) clearTimeout(e.id);
          this.dismissTimers.delete(id);
        }
      }
      for (const t of this.service.items()) {
        if (!this.dismissTimers.has(t.id)) this.startTimer(t.id, WIN_TOAST_AUTO_DISMISS_MS);
      }
    });

    destroyRef.onDestroy(() => {
      for (const [, entry] of this.dismissTimers) {
        if (entry.id !== null) clearTimeout(entry.id);
      }
      this.dismissTimers.clear();
    });
  }

  private startTimer(id: string, delay: number): void {
    if (this.hovering) {
      // Stack is hovered — store with null id; onMouseLeave will arm the timeout.
      this.dismissTimers.set(id, { id: null, remaining: delay, startTime: 0 });
    } else {
      const entry = {
        id: setTimeout(() => this.service.dismiss(id), delay) as ReturnType<typeof setTimeout> | null,
        remaining: delay,
        startTime: Date.now(),
      };
      this.dismissTimers.set(id, entry);
    }
  }

  onMouseEnter(): void {
    this.hovering = true;
    const now = Date.now();
    for (const [, entry] of this.dismissTimers) {
      if (entry.id !== null) {
        clearTimeout(entry.id);
        entry.remaining = Math.max(0, entry.remaining - (now - entry.startTime));
        entry.id = null;
      }
    }
  }

  onMouseLeave(): void {
    this.hovering = false;
    const now = Date.now();
    for (const [id, entry] of this.dismissTimers) {
      if (entry.id === null && entry.remaining > 0) {
        entry.id = setTimeout(() => this.service.dismiss(id), entry.remaining);
        entry.startTime = now;
      }
    }
  }
}
